import { err, ok, Result } from '@nx-paeonia/utils-result';
import type { AnimationSequence } from 'motion';
import { animate, stagger } from 'motion';

export const MENU_SELECTOR = '#mobile-menu' as const;

export enum MobileMenuProperties {
  offsetTop = '--offset-top',
}

export function getHeightOfMenu(): Result<number, string> {
  const headerElement = document.querySelector<HTMLElement>('.header');

  if (!headerElement) {
    return err('Header element not found');
  }

  return ok(headerElement.getBoundingClientRect().y + headerElement.getBoundingClientRect().height);
}

export function setVariableOnMobileMenu(variableName: MobileMenuProperties, value: string): void {
  const mobileMenuElement = document.querySelector<HTMLElement>(MENU_SELECTOR);

  if (mobileMenuElement) {
    mobileMenuElement.style.setProperty(variableName, value);
  }
}

export function setOpenState(isOpen: boolean): void {
  const menu = document.querySelector<HTMLDialogElement>(MENU_SELECTOR);

  if (!menu) {
    return;
  }

  if (isOpen) {
    menu.show();
  } else {
    menu.close();
  }
}

export function animateMenuOpen(): void {
  const defaultStagger = 0.2 as const;

  const animationSequence: AnimationSequence = [
    ['#mobile-menu .submenu', { height: 0 }, { duration: 0 }],
    ['#mobile-menu .submenu li', { opacity: 0 }, { duration: 0 }],
    ['#mobile-menu .mobile-menu-extra-2', { opacity: 1 }, { duration: 0 }],
    ['#mobile-menu .mobile-menu-extra-1', { opacity: 1 }, { duration: 0 }],
    ['#mobile-menu', { translateX: ['-100%', 0] }],

    // Animate menu items
    ['#mobile-menu .mobile-menu-main-item', { translateX: ['-200%', 0] }, { delay: stagger(defaultStagger) }],
    ['#mobile-menu button svg path', { pathLength: [0, 1] }, { delay: stagger(defaultStagger) }],
    ['#mobile-menu .mobile-menu-main-item .line', { scaleX: [0, 1] }, { delay: stagger(defaultStagger), at: '<' }],

    // Animate extra-menu-items
    ['#mobile-menu .mobile-menu-extra-1 h3', { translateY: [20, 0], opacity: [0, 1] }],
    ['#mobile-menu .mobile-menu-extra-1 a', { translateY: [20, 0], opacity: [0, 1] }, { delay: stagger(defaultStagger) }],

    ['#mobile-menu .mobile-menu-extra-2 h3', { translateY: [20, 0], opacity: [0, 2] }],
    ['#mobile-menu .mobile-menu-extra-2 a', { translateY: [20, 0], opacity: [0, 1] }, { delay: stagger(defaultStagger) }],

    // Animate image
    ['#mobile-menu img', { opacity: [0, 1] }, { delay: stagger(defaultStagger) }],
  ];
  const animation = animate(animationSequence, { duration: 3 });

  // Make a nice animated show.
  animation.cancel();
  setOpenState(true);
  animation.play();
}

export function animateMenuClose(): void {
  const defaultStagger = 0.2 as const;
  const animationSequence: AnimationSequence = [
    // Animate image
    ['#mobile-menu img', { opacity: [1, 0] }, { delay: stagger(defaultStagger) }],

    // Animate extra menu items
    ['#mobile-menu .mobile-menu-extra-2', { translateY: [0, 20], opacity: [1, 0], at: '<' }],
    ['#mobile-menu .mobile-menu-extra-1', { translateY: [0, 20], opacity: [1, 0] }],

    // Animate menu items
    ['#mobile-menu button svg path', { pathLength: [1, 0] }, { delay: stagger(defaultStagger), at: '<' }],
    ['#mobile-menu .mobile-menu-main-item .line', { scaleX: [1, 0] }, { delay: stagger(defaultStagger), at: '<' }],
    ['#mobile-menu .mobile-menu-main-item', { translateX: [0, '-200%'] }, { delay: stagger(defaultStagger) }],

    // Animate menu
    ['#mobile-menu', { translateX: [0, '-100%'] }],
  ];

  const animation = animate(animationSequence, { duration: 2 });

  animation.cancel();
  animation.finished.then(() => {
    setOpenState(false);
  });
  animation.play();
}

export function animateSubmenuOpen(element: HTMLElement): void {
  const submenu = document.querySelector<HTMLElement>(`#${element.getAttribute('aria-controls')}`);

  if (!submenu) {
    return;
  }

  const animationSequence: AnimationSequence = [
    [submenu, { height: 0 }, { duration: 0 }],
    [submenu.querySelectorAll('li'), { opacity: 0 }, { duration: 0 }],

    // Start of the animation
    [element, { rotate: [0, -90] }, { duration: 0.2 }],
    [submenu, { height: submenu.scrollHeight }, { duration: 0.2, at: '<' }],
    [submenu.querySelectorAll('li'), { opacity: [0, 1], translateX: [10, 0] }, { duration: 0.2, delay: stagger(0.1) }],
  ];

  const animation = animate(animationSequence);
  animation.cancel();

  animation.play();
  element.setAttribute('aria-expanded', 'true');
}

export function animateSubmenuClose(element: HTMLElement): void {
  const submenu = document.querySelector<HTMLElement>(`#${element.getAttribute('aria-controls')}`);

  if (!submenu) {
    return;
  }

  const menuItems = Array.from(submenu.querySelectorAll('li')).reverse();

  const animationSequence: AnimationSequence = [
    [submenu, { height: submenu.scrollHeight }, { duration: 0.2 }],

    // Start of the animation
    [menuItems, { opacity: [1, 0], translateX: [0, -10] }, { duration: 0.2, delay: stagger(0.1) }],
    [submenu, { height: 0 }, { duration: 0.2 }],
    [element, { rotate: [-90, 0] }, { duration: 0.2 }],
  ];

  const animation = animate(animationSequence);
  animation.cancel();

  animation.play();
  element.setAttribute('aria-expanded', 'false');
}
